import React, { memo, useMemo, useState } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { useClickOutside } from 'shared/hooks';
import { Chip, Icon, UiButton } from 'shared/ui';
import { TaskFilters as Filters, ChangeTaskFilter } from 'entities/task';

import { FilterType } from './FilterType/FilterType';
import { ProjectsFilter } from './ProjectsFilter/ProjectsFilter';
import { ShowCompleted } from './ShowCompleted/ShowCompleted';
import cls from './TaskExtraFilters.module.css';
import { TaskObserversFilter } from '../../TaskObserversFilter';
import { getIsActiveTypesFilter } from '../lib/helpers/getIsActiveTypesFilter';
import { useTaskExtraFilters } from '../lib/hooks/useTaskExtraFilters';

interface TaskExtraFiltersProps {
    classes?: string;
    queryParams: URLSearchParams;
    filteredData: Filters;
    onFilteredData: ChangeTaskFilter;
}

export const TaskExtraFilters = memo(
    ({ classes, queryParams, onFilteredData, filteredData }: TaskExtraFiltersProps) => {
        const { isShow, changeShow, selectedFilter, onSelectedFilter, setIsShow } =
            useTaskExtraFilters();
        const { rootRef } = useClickOutside({ isOpen: isShow, setIsOpen: setIsShow });
        const [isHovered, setIsHovered] = useState(false);

        const { t } = useTranslation();
        const isActiveTypesFilter = getIsActiveTypesFilter(filteredData.type);

        const isActiveFilter =
            !!filteredData.estimate ||
            !!filteredData.projects ||
            isActiveTypesFilter ||
            !!filteredData.executors.length;

        const fillColor = useMemo(() => {
            if (isHovered && !isActiveFilter) return '#1D2534';
            if (isHovered && isActiveFilter) return '#0E79C9';
            if (!isHovered && isActiveFilter) return '#1094F7';
            return undefined;
        }, [isHovered, isActiveFilter]);

        const initialTab = queryParams.get('tab') || 'taskBoard';

        return (
            <div className={classNames(cls.TaskExtraFilters, [classes])} ref={rootRef}>
                <div
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                >
                    <Chip
                        textContent={t('TaskExtraFilters.return.TaskExtraFilters.title')}
                        containerStyles={classNames(
                            cls.TaskExtraFiltersChipContainer,
                            isHovered && cls.isChipVisible
                        )}
                        textStyles={cls.TaskExtraFiltersChipText}
                    />
                    <UiButton
                        onClick={changeShow}
                        variant="icon"
                        startIcon={<Icon iconName="taskFilter" fill={fillColor} />}
                    />
                </div>
                <div
                    className={classNames(cls.dropdown, {
                        [cls.open]: isShow,
                    })}
                >
                    {initialTab === 'taskList' && <ShowCompleted />}

                    <ProjectsFilter
                        filteredData={filteredData}
                        onFilteredData={onFilteredData}
                        isShow={selectedFilter === 'project'}
                        onSelectedFilter={onSelectedFilter}
                    />
                    <TaskObserversFilter
                        isShow={selectedFilter === 'executors'}
                        filteredData={filteredData}
                        onFilteredData={onFilteredData}
                        onSelectedFilter={onSelectedFilter}
                    />
                    <FilterType
                        filteredData={filteredData}
                        onFilteredData={onFilteredData}
                        isShow={selectedFilter === 'type'}
                        onSelectedFilter={onSelectedFilter}
                    />
                </div>
            </div>
        );
    }
);
